/**
 * Groups the flat category list by its `group` field.
 *
 * @param {Array<{value: string, label: string, group: string}>} categories
 * @returns {Array<{group: string, items: Array<{label: string, value: string}>}>}
 */
export const toGroupedOptions = (categories) => {
    return categories.reduce((acc, { group, label, value }) => {
        // Find (or create) the container for this group
        let container = acc.find((g) => g.group === group);
        if (!container) {
            container = { group, items: [] };
            acc.push(container);
        }

        // Push the item with only the fields UI needs
        container.items.push({ label, value });
        return acc;
    }, []);
};

// Shared cache for look‑ups.  WeakMap → { [lowerValue]: badgeColor }
const enumColorCache = new WeakMap();

/**
 * Fast colour lookup for enum‑style option arrays.
 *
 * @template T
 * @param {T[]} enumArray                  – the original constant array
 * @param {string} value                   – case‑insensitive value to look up
 * @param {(item:T)=>string} valueSelector – how to read the option's value
 * @param {(item:T)=>string} colorSelector – how to read the option's colour
 * @param {Map<string,string>} [overrides] – optional runtime overrides
 * @returns {string|undefined}             – badge colour, if found
 */
export function getEnumColor(
    enumArray,
    value,
    valueSelector = (item) => item.value,
    colorSelector = (item) => item.badgeColor,
    overrides
) {
    // 1️⃣  Build (or fetch) the cached lookup table for this enum array
    let lookup = enumColorCache.get(enumArray);

    if (!lookup) {
        lookup = Object.create(null); // bare object: no proto look‑ups
        for (const item of enumArray) {
            lookup[valueSelector(item).toLowerCase()] = colorSelector(item);
        }
        enumColorCache.set(enumArray, lookup); // cache for future calls
    }

    // 2️⃣  Perform the lookup
    const key = value?.toLowerCase();
    if (overrides?.size) {
        const override = overrides.get(key);
        if (override !== undefined) return override;
    }
    return lookup[key] || 'gray';
}

/**
 * WeakMap cache keyed by the enum array **instance**.
 * Each entry holds an object:  { [lowerCaseValue]: label }
 * As soon as the array goes out of scope, its cache entry is GC’d automatically.
 */
const enumLabelCache = new WeakMap();

/**
 * Retrieves a human‑friendly label for an enum‑style option array.
 *
 * @template T
 * @param {T[]} enumArray                       the source array (e.g. campaignStatusOptions)
 * @param {string} value                       value to look up (case‑insensitive)
 * @param {(item:T)=>string} [valueSelector]   selector for the option’s value prop
 * @param {(item:T)=>string} [labelSelector]   selector for the option’s label prop
 * @param {Map<string,string>} [overrides]     optional per‑call override map
 * @returns {string|undefined}                 matched label, if any
 */
export function getEnumLabel(
    enumArray,
    value,
    valueSelector = (item) => item.value,
    labelSelector = (item) => item.label,
    overrides
) {
    // 1️⃣  Build (or grab) the cached lookup for this array
    let lookup = enumLabelCache.get(enumArray);

    if (!lookup) {
        lookup = Object.create(null);
        for (const item of enumArray) {
            lookup[valueSelector(item).toLowerCase()] = labelSelector(item);
        }
        enumLabelCache.set(enumArray, lookup);
    }

    // 2️⃣  Resolve the label
    const key = value?.toLowerCase();
    if (overrides?.size && overrides.has(key)) {
        return overrides.get(key);
    }
    return lookup[key];
}
